import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { LookupService } from '../../../../../_shared/services/lookup.service';
import { Router } from '@angular/router';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { PartsAndInventoryService } from '../../../services/parts-and-inventory.service';
import { CreatePartRequest } from '../../../models/create-part-request.model';
import { ToastrService } from 'ngx-toastr';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Option } from '../../../models/option.model';
import { LookupPartType } from '../../../../../_shared/models/lookup.model';

@Component({
  selector: 'app-add-part',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgbModule, TranslocoDirective, TranslocoPipe],
  templateUrl: './add-part.component.html',
  styleUrl: './add-part.component.scss'
})
export class AddPartComponent implements OnInit {
  private readonly addStorageLocationRoute = '';

  form: FormGroup;
  isLoading = false;
  isMakesLoading = false;
  isPartTypesLoading = false;
  isCategoriesLoading = false;
  isStorageLocationsLoading = false;
  isMakeLoading = false;
  makeSearchTerm = '';
  modelSearchTerm = '';
  partTypeSearchTerm = '';
  storageLocationSearchTerm = '';

  makeOptions: Option[] = [];
  modelOptions: Option[] = [];
  productionYearOptions: Option[] = this.buildProductionYearOptions();
  partTypeOptions: Option[] = [];
  partTypes: LookupPartType[] = [];
  categoryOptions: Option[] = [];
  storageLocationOptions: Option[] = [];
  reorderPointOptions: Option[] = [
    { label: '5', value: '5' },
    { label: '10', value: '10' },
    { label: '15', value: '15' }
  ];
  reorderQuantityOptions: Option[] = [
    { label: '10', value: '10' },
    { label: '25', value: '25' },
    { label: '50', value: '50' }
  ];
  maximumQuantityOptions: Option[] = [
    { label: '25', value: '25' },
    { label: '50', value: '50' },
    { label: '100', value: '100' }
  ];

  private lookupService = inject(LookupService);
  private partsAndInventoryService = inject(PartsAndInventoryService);
  private router = inject(Router);
  private errorService = inject(ErrorHandlerService);
  private toastr = inject(ToastrService);
  private activeModal = inject(NgbActiveModal);
  private fb = inject(FormBuilder);
  private transloco = inject(TranslocoService);

  constructor() {
    this.form = this.fb.group({
      partName: ['', Validators.required],
      make: ['', Validators.required],
      model: ['', Validators.required],
      productionYear: ['', Validators.required],
      partNumber: ['', Validators.required],
      description: [''],
      partType: ['', Validators.required],
      category: ['', Validators.required],
      storageLocation: ['', Validators.required],
      reorderPoint: ['5', Validators.required],
      reorderQuantity: ['25', Validators.required],
      maximumQuantity: ['25', Validators.required]
    });
  }

  async ngOnInit(): Promise<void> {
    await Promise.all([this.loadMakes(), this.loadPartTypes(), this.loadPartCategories(), this.loadStorageLocations()]);
  }

  get makeLabel(): string {
    const selectedMake = String(this.form.get('make')?.value ?? '');
    if (!selectedMake && this.isMakesLoading) {
      return this.transloco.translate('parts.modals.dropdown.loadingMakes');
    }
    return this.makeOptions.find(option => option.value === selectedMake)?.label || this.transloco.translate('parts.modals.dropdown.selectMake');
  }

  get modelLabel(): string {
    const selectedModel = String(this.form.get('model')?.value ?? '');
    if (!selectedModel && this.isMakeLoading) {
      return this.transloco.translate('parts.modals.dropdown.loadingModels');
    }
    return this.modelOptions.find(option => option.value === selectedModel)?.label || this.transloco.translate('parts.modals.dropdown.selectModel');
  }

  get isMakeSelected(): boolean {
    return !!this.form.get('make')?.value;
  }

  get isModelEnabled(): boolean {
    return this.isMakeSelected && !this.isMakeLoading && this.modelOptions.length > 0;
  }

  get productionYearLabel(): string {
    const selectedYear = String(this.form.get('productionYear')?.value ?? '');
    return selectedYear || this.transloco.translate('parts.modals.placeholders.selectYear');
  }

  get partTypeLabel(): string {
    const selected = String(this.form.get('partType')?.value ?? '');
    if (!selected && this.isPartTypesLoading) {
      return this.transloco.translate('parts.modals.dropdown.loadingPartTypes');
    }
    return this.partTypeOptions.find(o => o.value === selected)?.label || this.transloco.translate('parts.modals.dropdown.selectPartType');
  }

  get categoryLabel(): string {
    const selectedCategory = String(this.form.get('category')?.value ?? '');
    if (!selectedCategory && this.isCategoriesLoading) {
      return this.transloco.translate('parts.modals.dropdown.loadingCategories');
    }
    return this.categoryOptions.find(option => option.value === selectedCategory)?.label || this.transloco.translate('parts.modals.dropdown.selectCategory');
  }

  get storageLocationLabel(): string {
    const selectedLocation = String(this.form.get('storageLocation')?.value ?? '');
    if (!selectedLocation && this.isStorageLocationsLoading) {
      return this.transloco.translate('parts.modals.dropdown.loadingStorageLocations');
    }
    return this.storageLocationOptions.find(option => option.value === selectedLocation)?.label || this.transloco.translate('parts.modals.dropdown.selectStorageLocation');
  }

  get reorderPointLabel(): string {
    return this.form.get('reorderPoint')?.value || this.transloco.translate('parts.modals.dropdown.select');
  }

  get reorderQuantityLabel(): string {
    return this.form.get('reorderQuantity')?.value || this.transloco.translate('parts.modals.dropdown.select');
  }

  get maximumQuantityLabel(): string {
    return this.form.get('maximumQuantity')?.value || this.transloco.translate('parts.modals.dropdown.select');
  }

  get filteredMakeOptions(): Option[] {
    const query = this.makeSearchTerm.trim().toLowerCase();
    if (!query) {
      return this.makeOptions;
    }

    return this.makeOptions.filter(option => option.label.toLowerCase().includes(query));
  }

  get filteredModelOptions(): Option[] {
    const query = this.modelSearchTerm.trim().toLowerCase();
    if (!query) {
      return this.modelOptions;
    }

    return this.modelOptions.filter(option => option.label.toLowerCase().includes(query));
  }

  get filteredPartTypeOptions(): Option[] {
    const query = this.partTypeSearchTerm.trim().toLowerCase();
    if (!query) {
      return this.partTypeOptions;
    }

    return this.partTypeOptions.filter(option => option.label.toLowerCase().includes(query));
  }

  get filteredStorageLocationOptions(): Option[] {
    const query = this.storageLocationSearchTerm.trim().toLowerCase();
    if (!query) {
      return this.storageLocationOptions;
    }

    return this.storageLocationOptions.filter(option => option.label.toLowerCase().includes(query));
  }

  get showNoStorageLocationSearchResult(): boolean {
    return !this.isStorageLocationsLoading && this.filteredStorageLocationOptions.length === 0 && this.storageLocationOptions.length > 0;
  }

  get showNoStorageLocationsAction(): boolean {
    return !this.isStorageLocationsLoading && this.storageLocationOptions.length === 0;
  }

  onSelect(field: string, value: string, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (field === 'make') {
      this.onMakeSelect(value);
      return;
    }

    this.form.get(field)?.setValue(value);
    this.form.get(field)?.markAsTouched();

    if (field === 'partType') {
      this.partTypeSearchTerm = '';
      const partType = this.partTypes.find(pt => String(pt.id) === value);
      if (partType) {
        this.form.get('category')?.setValue(String(partType.categoryId));
        this.form.get('category')?.markAsTouched();
      }
    }

    if (field === 'model') {
      this.modelSearchTerm = '';
    }

    if (field === 'storageLocation') {
      this.storageLocationSearchTerm = '';
    }
  }

  async onMakeSelect(makeId: string, event?: Event): Promise<void> {
    event?.preventDefault();
    event?.stopPropagation();

    const currentMake = String(this.form.get('make')?.value ?? '');
    this.form.get('make')?.setValue(makeId);
    this.form.get('make')?.markAsTouched();

    if (currentMake !== makeId) {
      this.form.get('model')?.setValue('');
      this.form.get('model')?.markAsUntouched();
      this.form.get('model')?.markAsPristine();
      this.modelOptions = [];
      this.modelSearchTerm = '';
      await this.loadModelsByMakeId(makeId);
    }

    this.makeSearchTerm = '';
  }

  closeModal(): void {
    this.activeModal.close();
  }

  onCancel(): void {
    this.closeModal();
  }

  async onAddStorageLocationClick(): Promise<void> {
    this.closeModal();
    await this.router.navigateByUrl(this.addStorageLocationRoute);
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => this.form.get(key)?.markAsTouched());
      return;
    }

    const request = this.buildCreatePartRequest();
    if (!request) {
      return;
    }

    this.isLoading = true;
    try {
      await this.partsAndInventoryService.createPartAsync(request);
      this.toastr.success(this.transloco.translate('parts.modals.addPart.toastCreated'));
      this.activeModal.close(true);
    } catch (error) {
      this.errorService.handleError(error);
    } finally {
      this.isLoading = false;
    }
  }

  private async loadMakes(): Promise<void> {
    this.isMakesLoading = true;
    try {
      const response = await this.lookupService.getMakesAsync();
      this.makeOptions = this.mapLookupItemsToOptions(response.data);
    } catch (error) {
      this.errorService.handleError(error);
    } finally {
      this.isMakesLoading = false;
    }
  }

  private async loadPartTypes(): Promise<void> {
    this.isPartTypesLoading = true;
    try {
      const response = await this.lookupService.getPartTypesAsync();
      this.partTypes = response.data ?? [];
      this.partTypeOptions = this.mapLookupItemsToOptions(this.partTypes);
    } catch (error) {
      this.errorService.handleError(error);
    } finally {
      this.isPartTypesLoading = false;
    }
  }

  private async loadPartCategories(): Promise<void> {
    this.isCategoriesLoading = true;
    try {
      const response = await this.lookupService.getPartCategoriesAsync();
      this.categoryOptions = this.mapLookupItemsToOptions(response.data);
    } catch (error) {
      this.errorService.handleError(error);
    } finally {
      this.isCategoriesLoading = false;
    }
  }

  private async loadStorageLocations(): Promise<void> {
    this.isStorageLocationsLoading = true;
    try {
      const response = await this.lookupService.getWarehousesAsync([]);
      this.storageLocationOptions = this.mapLookupItemsToOptions(response.data);
    } catch (error) {
      this.errorService.handleError(error);
    } finally {
      this.isStorageLocationsLoading = false;
    }
  }

  private async loadModelsByMakeId(makeId: string): Promise<void> {
    const parsedMakeId = Number(makeId);
    if (!Number.isFinite(parsedMakeId)) {
      return;
    }

    this.isMakeLoading = true;
    try {
      const response = await this.lookupService.getModelsByMakeIdAsync(parsedMakeId);
      this.modelOptions = this.mapLookupItemsToOptions(response.data);
    } catch (error) {
      this.errorService.handleError(error);
    } finally {
      this.isMakeLoading = false;
    }
  }

  private mapLookupItemsToOptions(items: any): Option[] {
    if (!Array.isArray(items)) {
      return [];
    }

    return items
      .map(item => {
        const id = item?.id ?? item?.makeId ?? item?.modelId ?? item?.value;
        const label = item?.name ?? item?.makeName ?? item?.modelName ?? item?.label;

        return {
          value: String(id ?? ''),
          label: String(label ?? '')
        } as Option;
      })
      .filter(option => option.value !== '' && option.label !== '');
  }

  private buildProductionYearOptions(yearsBack: number = 25): Option[] {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: yearsBack + 1 }, (_, i) => {
      const year = String(currentYear - i);
      return { label: year, value: year };
    });
  }

  private buildCreatePartRequest(): CreatePartRequest | null {
    const partName = String(this.form.get('partName')?.value ?? '').trim();
    const partNumber = String(this.form.get('partNumber')?.value ?? '').trim();
    const description = String(this.form.get('description')?.value ?? '').trim();

    const partTypeId = Number(this.form.get('partType')?.value);
    const makeId = Number(this.form.get('make')?.value);
    const modelId = Number(this.form.get('model')?.value);
    const categoryId = Number(this.form.get('category')?.value);
    const warehouseId = Number(this.form.get('storageLocation')?.value);

    const selectedYear = this.form.get('productionYear')?.value;
    const modelYear = Number(selectedYear);

    const reorderPoint = Number(this.form.get('reorderPoint')?.value);
    const reorderQuantity = Number(this.form.get('reorderQuantity')?.value);
    const maximumQuantity = Number(this.form.get('maximumQuantity')?.value);

    const numericValues = [partTypeId, makeId, modelId, categoryId, warehouseId, modelYear, reorderPoint, reorderQuantity, maximumQuantity];
    if (numericValues.some(value => !Number.isFinite(value))) {
      return null;
    }

    return {
      name: partName,
      partNumber,
      description,
      partTypeId,
      categoryId,
      makeId,
      modelId,
      warehouseId,
      modelYear,
      reorderPoint,
      reorderQuantity,
      maximumQuantity
    };
  }
}